import clsx from 'clsx';
import { CalendarPlus, FileSpreadsheet, Pencil, Plus, Trash2, Upload, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ABSENCE_STATUSES, HOLIDAY_CALENDARS, parseCsv } from '@pz/core';
import { DocumentsButton } from '../components/documents';
import { LeaveDialog, LeaveStatusPill, periodText, useExtentText } from '../components/leave';
import { Button, Checkbox, EmptyState, ErrorBox, Field, IconButton, Input, Loading, matchesSearch, Modal, Notice, PageHeader, Pill, Select, SortTh, Tabs, Textarea, td, th, useToast } from '../components/ui';
import { useTableSort } from '../lib/sort';
import { d, num } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { useMe } from '../lib/me';
import { saveFile } from '../lib/download';
import { rpc } from '../lib/rpc';
import { lang } from '../lib/format';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { OvertimeTab } from './Overtime';

type Tab = 'overview' | 'all' | 'overtime' | 'holidays' | 'events';

/**
 * Abwesenheiten und Feiertage (HR and administration): entitlements and balances, all absences with
 * status and documents, direct entry and import by HR, public holiday calendars and company events. Requests are
 * decided under "Abwesenheitsanträge"; the people request and report in "Meine Abwesenheiten".
 */
export function AbsencesPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const hr = can('hr.manage');
  const [tab, setTab] = useTabParam<Tab>(hr ? ['overview', 'all', 'overtime', 'holidays', 'events'] : ['holidays', 'events']);
  if (!can('hr.manage', 'settings.manage')) return <EmptyState>{t('absencesPage.noAccess')}</EmptyState>;
  return (
    <div>
      <PageHeader title={t('absencesPage.title')} subtitle={t('absencesPage.subtitle')} />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('leave.overviewTab'), hidden: !hr },
          { id: 'all', label: t('leave.allTab'), hidden: !hr },
          { id: 'overtime', label: t('overtime.tab'), hidden: !hr },
          { id: 'holidays', label: t('leave.holidaysTab') },
          { id: 'events', label: t('leave.eventsTab') },
        ]}
      />
      {tab === 'overview' && hr && <OverviewTab />}
      {tab === 'all' && hr && <AllTab />}
      {tab === 'overtime' && hr && <OvertimeTab />}
      {tab === 'holidays' && <HolidaysTab />}
      {tab === 'events' && <EventsTab />}
    </div>
  );
}

function OverviewTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const thisYear = Number(me.today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const q = useQ('leave.overview', { year });
  const [search, setSearch] = useState('');
  const rows = useMemo(() => (q.data?.rows ?? []).filter((r) => matchesSearch(`${r.name} ${r.discipline ?? ''} ${r.country ?? ''}`, search)), [q.data, search]);
  const chunk = useChunked(rows, 100, `${year}|${search}`);
  const save = useM('leave.setEntitlement', { invalidate: ['leave.'] });
  const [edit, setEdit] = useState<{ personId: string; name: string; unit: 'days' | 'hours'; amount: string; carryover: string; carryoverUntil: string; note: string } | null>(null);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Select className="w-32" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t('common.year')}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Input className="w-64" placeholder={t('absencesPage.searchPerson')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && rows.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
      {q.data && rows.length > 0 && (
        <ScrollTable label={t('leave.overviewTab')}>
          <table className="w-full min-w-[980px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('common.person')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.entitlement')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.balance.carryover')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.balance.taken')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.balance.planned')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.balance.requested')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.balance.available')}</th>
                <th className={clsx(th, 'text-right')}>{t('leave.sickDays')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((r) => {
                const u = t(`leave.units.${r.balance.unit}`);
                return (
                  <tr key={r.personId} className="border-b border-line/70">
                    <td className={td}>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-graphite">{[r.discipline, r.country].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className={clsx(td, 'num text-right')}>{r.entitlement ? `${num(r.entitlement.amount)} ${u}` : <span className="text-faint">-</span>}</td>
                    <td className={clsx(td, 'num text-right')} title={r.entitlement?.carryoverUntil ? t('leave.balance.carryoverUntil', { date: d(r.entitlement.carryoverUntil) }) : undefined}>
                      {r.balance.carryover ? num(r.balance.carryover) : ''}
                    </td>
                    <td className={clsx(td, 'num text-right')}>{num(r.balance.taken)}</td>
                    <td className={clsx(td, 'num text-right')}>{num(r.balance.planned)}</td>
                    <td className={clsx(td, 'num text-right')}>{r.balance.requested ? <Pill tone="warn">{num(r.balance.requested)}</Pill> : ''}</td>
                    <td className={clsx(td, 'num text-right font-semibold', r.balance.available !== null && r.balance.available < 0 && 'text-bad')}>{num(r.balance.available)}</td>
                    <td className={clsx(td, 'num text-right')}>{r.sickDays ? num(r.sickDays) : ''}</td>
                    <td className={clsx(td, 'text-right')}>
                      <IconButton
                        label={t('leave.editEntitlement')}
                        icon={<Pencil className="size-3.5" />}
                        onClick={() =>
                          setEdit({
                            personId: r.personId,
                            name: r.name,
                            unit: (r.entitlement?.unit as 'days') ?? 'days',
                            amount: String(r.entitlement?.amount ?? ''),
                            carryover: String(r.entitlement?.carryover ?? 0),
                            carryoverUntil: r.entitlement?.carryoverUntil ?? '',
                            note: r.entitlement?.note ?? '',
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
      )}
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit ? `${t('leave.editEntitlement')}: ${edit.name} (${year})` : ''}
        footer={
          <>
            <Button onClick={() => setEdit(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!edit || edit.amount === '' || Number.isNaN(Number(edit.amount.replace(',', '.')))}
              loading={save.isPending}
              onClick={() =>
                edit &&
                save
                  .mutateAsync({
                    personId: edit.personId,
                    year,
                    unit: edit.unit,
                    amount: Number(edit.amount.replace(',', '.')),
                    carryover: Number(edit.carryover.replace(',', '.')) || 0,
                    carryoverUntil: edit.carryoverUntil || null,
                    note: edit.note || null,
                  })
                  .then(() => (toast.ok(t('common.saved')), setEdit(null)))
                  .catch(() => {})
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {edit && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
              <Field label={t('leave.unit')}>
                <Select value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value as 'days' })} className="w-32">
                  <option value="days">{t('leave.units.days')}</option>
                  <option value="hours">{t('leave.units.hours')}</option>
                </Select>
              </Field>
              <Field label={t('leave.amount')}>
                <Input inputMode="decimal" className="num w-28" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} />
              </Field>
              <Field label={t('leave.carryover')}>
                <Input inputMode="decimal" className="num w-28" value={edit.carryover} onChange={(e) => setEdit({ ...edit, carryover: e.target.value })} />
              </Field>
              <Field label={t('leave.carryoverUntil')}>
                <Input type="date" value={edit.carryoverUntil} onChange={(e) => setEdit({ ...edit, carryoverUntil: e.target.value })} />
              </Field>
            </div>
            <p className="text-xs text-graphite">{t('leave.carryoverHint')}</p>
            <Field label={t('common.note')}>
              <Input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} maxLength={300} />
            </Field>
            <ErrorBox error={save.error} />
          </div>
        )}
      </Modal>
    </div>
  );
}

function AllTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const year = me.today.slice(0, 4);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [status, setStatus] = useState('');
  const q = useQ('absences.list', { from, to, status: (status || undefined) as 'approved' | undefined });
  const [search, setSearch] = useState('');
  // sorting by column, personal (request 14 of round 4)
  type Row = NonNullable<typeof q.data>['absences'][number];
  const sorting = useTableSort<Row>('absences.all', {
    person: (a) => a.personName,
    period: (a) => a.from,
    kind: (a) => t(`week.absence.${a.kind as 'vacation'}`),
    extent: (a) => (a.minutes ? a.minutes / 60 / 8 : a.days),
    status: (a) => a.status,
    documents: (a) => a.documents.length,
  });
  const list = useMemo(
    () => sorting.apply((q.data?.absences ?? []).filter((a) => matchesSearch(`${a.personName} ${a.note ?? ''}`, search))),
    [q.data, search, sorting.sort], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const chunk = useChunked(list, 100, `${from}|${to}|${status}|${search}|${sorting.sort?.key}|${sorting.sort?.dir}`);
  const inv = { invalidate: ['absences.', 'leave.', 'workload.', 'time.', 'kpi.'] };
  const cancel = useM('absences.cancel', inv);
  const imp = useM('absences.import', inv);
  const extent = useExtentText();
  const [open, setOpen] = useState<'new' | 'import' | null>(null);
  const [cancelFor, setCancelFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState<{ email: string; kind: string; from: string; to: string; halfDay?: boolean; externalRef?: string | null }[]>([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<string | null>(null);
  // all absences of the period as an Excel file (item 11), with the same rights as the list
  const [exporting, setExporting] = useState(false);
  const exportAll = async () => {
    setExporting(true);
    try {
      saveFile(await rpc('absences.export', { from, to, status: (status || undefined) as 'approved' | undefined, lang: lang() }));
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };

  const onFile = async (file: File) => {
    const text = await file.text();
    const parsed = parseCsv(text);
    const [head, ...body] = parsed;
    const idx = (n: string) => head.findIndex((x) => x.trim().toLowerCase() === n.toLowerCase());
    setFileName(file.name);
    setRows(
      body.map((r) => ({
        email: r[idx('email')] ?? '',
        kind: r[idx('kind')] ?? 'vacation',
        from: r[idx('from')] ?? '',
        to: r[idx('to')] ?? '',
        halfDay: ['1', 'true', 'ja', 'yes'].includes((r[idx('halfDay')] ?? '').toLowerCase()),
        externalRef: idx('externalRef') >= 0 ? r[idx('externalRef')] : null,
      })),
    );
    setResult(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet px-3 py-2">
        <Field label={t('common.from')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('common.to')}>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label={t('leave.statusFilter')}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
            <option value="">{t('leave.allStatuses')}</option>
            {ABSENCE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`leave.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.search')}>
          <Input className="w-56" placeholder={t('absencesPage.searchPerson')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <div className="ml-auto flex gap-2">
          <Button icon={<FileSpreadsheet className="size-4" />} loading={exporting} onClick={exportAll}>
            {t('common.exportExcel')}
          </Button>
          <Button icon={<Upload className="size-4" />} onClick={() => setOpen('import')}>
            {t('workload.import')}
          </Button>
          <Button icon={<CalendarPlus className="size-4" />} onClick={() => setOpen('new')}>
            {t('leave.hrTitle')}
          </Button>
        </div>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && list.length === 0 && <EmptyState>{q.data.absences.length ? t('common.noResults') : t('common.noData')}</EmptyState>}
      {list.length > 0 && (
        <ScrollTable label={t('leave.allTab')}>
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <SortTh label={t('common.person')} sortKey="person" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('leave.period')} sortKey="period" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('leave.kind')} sortKey="kind" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('leave.extent')} sortKey="extent" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('leave.state')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('documents.column')} sortKey="documents" sort={sorting.sort} onSort={sorting.toggle} />
                <th className={th}>{t('leave.note')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((a) => (
                <tr key={a.id} className={clsx('border-b border-line/70', (a.status === 'cancelled' || a.status === 'rejected') && 'text-graphite')}>
                  <td className={td}>{a.personName}</td>
                  <td className={clsx(td, 'num whitespace-nowrap')}>{periodText(a.from, a.to)}</td>
                  <td className={td}>{t(`week.absence.${a.kind as 'vacation'}`)}</td>
                  <td className={clsx(td, 'num')}>{extent(a)}</td>
                  <td className={td}>
                    <LeaveStatusPill status={a.status} />
                    {a.decidedBy && <div className="mt-0.5 text-xs text-graphite">{t('leave.decidedBy', { name: a.decidedBy })}</div>}
                  </td>
                  <td className={td}>
                    <DocumentsButton
                      absenceId={a.id}
                      count={a.documents.length}
                      expected={a.documentExpected && (a.status === 'requested' || a.status === 'approved')}
                      title={`${a.personName}: ${t(`week.absence.${a.kind as 'vacation'}`)} ${periodText(a.from, a.to)}`}
                    />
                  </td>
                  <td className={clsx(td, 'max-w-xs text-xs')}>
                    {a.note && <div>{a.note}</div>}
                    {a.decisionNote && <div className="text-graphite">{a.decisionNote}</div>}
                  </td>
                  <td className={clsx(td, 'text-right')}>
                    {(a.status === 'approved' || a.status === 'requested') && (
                      <IconButton label={t('leave.cancel')} icon={<XCircle className="size-3.5" />} onClick={() => (setReason(''), setCancelFor(a.id))} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
      )}
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      <LeaveDialog open={open === 'new'} mode="hr" onClose={() => setOpen(null)} />
      <Modal
        open={!!cancelFor}
        onClose={() => setCancelFor(null)}
        title={t('leave.cancelTitle')}
        footer={
          <>
            <Button onClick={() => setCancelFor(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={cancel.isPending}
              onClick={() =>
                cancelFor &&
                cancel
                  .mutateAsync({ id: cancelFor, reason: reason.trim() })
                  .then(() => setCancelFor(null))
                  .catch(toast.error)
              }
            >
              {t('leave.cancel')}
            </Button>
          </>
        }
      >
        <Field label={t('leave.cancelReason')}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
      </Modal>
      <Modal
        open={open === 'import'}
        onClose={() => setOpen(null)}
        title={t('workload.importTitle')}
        footer={
          <>
            <Button onClick={() => setOpen(null)}>{t('common.close')}</Button>
            <Button
              variant="primary"
              disabled={!rows.length}
              loading={imp.isPending}
              onClick={() =>
                imp
                  .mutateAsync({ fileName, rows })
                  .then((r) => setResult(t('workload.importResult', { ok: r.imported, errors: r.errors.length }) + (r.errors.length ? `: ${r.errors.slice(0, 5).map((e) => `#${e.row} ${t(`errors.${e.code}`, { email: e.email ?? '' })}`).join(', ')}` : '')))
                  .catch(toast.error)
              }
            >
              {t('workload.import')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-graphite">{t('leave.importHint')}</p>
          <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} className="text-sm" />
          {rows.length > 0 && <Notice tone="neutral">{t('workload.rowsFound', { count: rows.length })}</Notice>}
          {result && <Notice tone="good">{result}</Notice>}
        </div>
      </Modal>
    </div>
  );
}

type EventForm = {
  id?: string;
  from: string;
  to: string;
  title: string;
  country: '' | 'DE' | 'TR';
  part: 'full' | 'am' | 'pm' | 'time';
  startTime: string;
  endTime: string;
  note: string;
  personIds: string[];
};

/**
 * Company events (request 27 of round 4): whole days, half a day or a time range, a note and the people taking part.
 * Participants see the event in their row of the team calendar, on the start page and in their Outlook calendar; an
 * event without participants is for everybody (of the country). Kept here with the holidays, where HR plans the year.
 */
function EventsTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const thisYear = Number(me.today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const q = useQ('companyEvents.list', { year });
  const inv = { invalidate: ['companyEvents.', 'leave.team', 'home.', 'dashboard.'] };
  const set = useM('companyEvents.set', inv);
  const del = useM('companyEvents.delete', inv);
  const [form, setForm] = useState<EventForm | null>(null);
  const people = useQ('people.list', {}, { enabled: !!form });
  const [who, setWho] = useState('');
  const single = !!form && form.from === form.to;
  const valid =
    !!form && !!form.title.trim() && !!form.from && !!form.to && form.from <= form.to && (form.part !== 'time' || (single && !!form.startTime && !!form.endTime && form.startTime < form.endTime));
  const timeText = (e: { part: string; startTime: string | null; endTime: string | null }) =>
    e.part === 'time' && e.startTime && e.endTime ? `${e.startTime}–${e.endTime}` : e.part === 'am' || e.part === 'pm' ? t(`leave.parts.${e.part}`) : t('leave.events.parts.full');
  const save = (f: EventForm) =>
    set
      .mutateAsync({
        id: f.id,
        from: f.from,
        to: f.to,
        title: f.title.trim(),
        country: f.country || null,
        part: f.from === f.to ? f.part : 'full',
        startTime: f.part === 'time' ? f.startTime : null,
        endTime: f.part === 'time' ? f.endTime : null,
        note: f.note.trim() || null,
        personIds: f.personIds,
      })
      .then(() => {
        setForm(null);
        toast.ok(t('common.saved'));
      })
      .catch(() => {});
  const remove = (id: string, title: string) =>
    del
      .mutateAsync({ id })
      .then((r) =>
        toast.undoable(t('leave.events.deleted', { title }), t('common.undo'), () =>
          set.mutateAsync({ ...r.restore, country: r.restore.country ?? null }).then(() => toast.ok(t('common.undone'))).catch(toast.error),
        ),
      )
      .catch(toast.error);
  const candidates = (people.data?.people ?? []).filter((p) => p.active && p.kind === 'employee' && (!form?.country || p.locationCountry === form.country));
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('leave.events.hint')}</Notice>
      <div className="flex items-center gap-3">
        <Select className="w-32" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t('common.year')}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        {q.data?.canEdit && (
          <Button
            className="ml-auto"
            icon={<Plus className="size-4" />}
            onClick={() => setForm({ from: me.today, to: me.today, title: '', country: '', part: 'full', startTime: '09:00', endTime: '12:00', note: '', personIds: [] })}
          >
            {t('leave.events.new')}
          </Button>
        )}
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && q.data.events.length === 0 && <EmptyState>{t('common.noData')}</EmptyState>}
      {!!q.data?.events.length && (
        <div className="scroll-x rounded-lg border border-line bg-sheet">
          <table className="w-full text-sm">
            <tbody>
              {q.data.events.map((e) => (
                <tr key={e.id} className="border-b border-line/70">
                  <td className={clsx(td, 'num whitespace-nowrap')}>
                    {periodText(e.from, e.to)}
                    <span className="block text-xs text-graphite">{timeText(e)}</span>
                  </td>
                  <td className={td}>
                    {e.title}
                    {e.note && <span className="block text-xs whitespace-pre-line text-graphite">{e.note}</span>}
                  </td>
                  <td className={td}>
                    <span className="flex flex-wrap gap-1">
                      <Pill>{e.country ?? t('leave.events.allCountries')}</Pill>
                      {e.participants.length > 0 ? (
                        <Pill tone="info" title={e.participants.map((x) => x.name).join(', ')}>
                          {t('leave.events.participantsCount', { count: e.participants.length })}
                        </Pill>
                      ) : (
                        <Pill>{t('leave.events.everybody')}</Pill>
                      )}
                    </span>
                  </td>
                  <td className={clsx(td, 'text-right')}>
                    {q.data.canEdit && (
                      <div className="flex justify-end gap-1">
                        <IconButton
                          label={t('common.edit')}
                          icon={<Pencil className="size-3.5" />}
                          onClick={() =>
                            setForm({
                              id: e.id,
                              from: e.from,
                              to: e.to,
                              title: e.title,
                              country: (e.country as 'DE') ?? '',
                              part: e.part,
                              startTime: e.startTime ?? '09:00',
                              endTime: e.endTime ?? '12:00',
                              note: e.note ?? '',
                              personIds: e.participants.map((x) => x.id),
                            })
                          }
                        />
                        <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => remove(e.id, e.title)} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        wide
        title={form?.id ? t('leave.events.edit') : t('leave.events.new')}
        footer={
          <>
            <Button onClick={() => setForm(null)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!valid} loading={set.isPending} onClick={() => form && void save(form)}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {form && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-3">
              <Field label={t('leave.events.name')}>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} />
              </Field>
              <div className="flex flex-wrap gap-3">
                <Field label={t('common.from')}>
                  <Input type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value, to: e.target.value > form.to ? e.target.value : form.to })} />
                </Field>
                <Field label={t('common.to')}>
                  <Input type="date" value={form.to} min={form.from} onChange={(e) => setForm({ ...form, to: e.target.value })} />
                </Field>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <Field label={t('leave.events.part')} hint={!single ? t('leave.events.partHint') : undefined}>
                  <Select value={single ? form.part : 'full'} disabled={!single} onChange={(e) => setForm({ ...form, part: e.target.value as 'full' })} className="w-44">
                    <option value="full">{t('leave.events.parts.full')}</option>
                    <option value="am">{t('leave.parts.am')}</option>
                    <option value="pm">{t('leave.parts.pm')}</option>
                    <option value="time">{t('leave.events.parts.time')}</option>
                  </Select>
                </Field>
                {single && form.part === 'time' && (
                  <>
                    <Field label={t('leave.events.start')}>
                      <Input type="time" step={900} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="w-28" />
                    </Field>
                    <Field label={t('leave.events.end')}>
                      <Input type="time" step={900} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="w-28" />
                    </Field>
                  </>
                )}
              </div>
              <Field label={t('leave.events.country')}>
                <Select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value as 'DE' })} className="w-44">
                  <option value="">{t('leave.events.allCountries')}</option>
                  <option value="DE">{t('people.countries.DE')}</option>
                  <option value="TR">{t('people.countries.TR')}</option>
                </Select>
              </Field>
              <Field label={t('leave.note')}>
                <Textarea rows={3} value={form.note} maxLength={500} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t('leave.events.notePlaceholder')} />
              </Field>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t('leave.events.participants')}</span>
                <span className="text-xs text-graphite">{form.personIds.length ? t('leave.events.participantsCount', { count: form.personIds.length }) : t('leave.events.everybody')}</span>
                {form.personIds.length > 0 && (
                  <button type="button" className="ml-auto text-xs text-graphite underline-offset-2 hover:text-ink hover:underline" onClick={() => setForm({ ...form, personIds: [] })}>
                    {t('days.clear')}
                  </button>
                )}
              </div>
              <Input value={who} onChange={(e) => setWho(e.target.value)} placeholder={t('leave.events.searchPerson')} />
              <ul className="max-h-72 overflow-y-auto rounded-md border border-line p-1">
                {people.isLoading && <Loading />}
                {candidates
                  .filter((p) => matchesSearch(`${p.displayName} ${p.discipline ?? ''}`, who))
                  .map((p) => (
                    <li key={p.id}>
                      <Checkbox
                        className="rounded px-2 py-1 hover:bg-sheet-2"
                        label={
                          <span>
                            {p.displayName} <span className="text-xs text-graphite">{[p.discipline, p.locationCountry].filter(Boolean).join(' · ')}</span>
                          </span>
                        }
                        checked={form.personIds.includes(p.id)}
                        onChange={(e) => setForm({ ...form, personIds: e.target.checked ? [...form.personIds, p.id] : form.personIds.filter((x) => x !== p.id) })}
                      />
                    </li>
                  ))}
              </ul>
              <p className="text-xs text-graphite">{t('leave.events.participantsHint')}</p>
            </div>
            <div className="md:col-span-2">
              <ErrorBox error={set.error} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

type HolidayForm = { calendar: string; date: string; name: string; fraction: 0 | 0.5 | 1; yearly: boolean; note: string; moveFrom: string | null; standard: boolean };

/**
 * Public holidays (request 26 of round 4): every calendar (TR, DE-...) and all calendars together are fully editable.
 * Standard days of a calendar can be renamed, moved, set to half a day or taken away (and restored); own days can be
 * added once or every year. Every change shows a notice with an undo.
 */
function HolidaysTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [calendar, setCalendar] = useState<string>('DE-NW');
  const [year, setYear] = useState(Number(me.today.slice(0, 4)));
  const q = useQ('holidays.list', { calendar, year });
  const inv = { invalidate: ['holidays.', 'workload.', 'time.', 'leave.', 'kpi.', 'dashboard.'] };
  const set = useM('holidays.set', inv);
  const del = useM('holidays.delete', inv);
  const revert = useM('holidays.revert', inv);
  const [form, setForm] = useState<HolidayForm | null>(null);
  const years = useMemo(() => [year - 1, year, year + 1], [year]);
  type Change = { calendar: string; date: string; row: { name: string; fraction: number; note: string | null; yearly: boolean } | null };
  const undoable = (text: string, changes: Change[]) => {
    if (!changes.length) return toast.ok(text);
    toast.undoable(text, t('common.undo'), () => revert.mutateAsync({ changes }).then(() => toast.ok(t('common.undone'))).catch(toast.error));
  };
  const save = (f: HolidayForm) =>
    set
      .mutateAsync({ calendar: f.calendar, date: f.date, name: f.name, fraction: f.fraction, yearly: f.yearly, note: f.note || null, moveFrom: f.moveFrom })
      .then((r) => {
        setForm(null);
        undoable(t('holidays.saved'), r.changes);
      })
      .catch(() => {});
  const remove = (h: { date: string; name: string }) =>
    set
      .mutateAsync({ calendar, date: h.date, name: h.name, fraction: 0 })
      .then((r) => undoable(t('holidays.removed', { name: h.name }), r.changes))
      .catch(toast.error);
  const dropOverride = (id: string, text: string) =>
    del
      .mutateAsync({ id })
      .then((r) => undoable(text, r.changes))
      .catch(toast.error);
  const edit = q.data?.canEdit;
  const SOURCE_TONE = { standard: 'neutral', changed: 'warn', custom: 'accent' } as const;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet px-3 py-2">
        <Field label={t('workload.calendar')}>
          <Select value={calendar} onChange={(e) => setCalendar(e.target.value)}>
            {HOLIDAY_CALENDARS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.year')}>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </Field>
        {edit && (
          <Button
            className="ml-auto"
            icon={<Plus className="size-4" />}
            onClick={() => setForm({ calendar, date: `${year}-12-24`, name: '', fraction: 1, yearly: false, note: '', moveFrom: null, standard: false })}
          >
            {t('holidays.add')}
          </Button>
        )}
      </div>
      <p className="text-xs text-graphite">{t('holidays.intro')}</p>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="rounded-lg border border-line bg-sheet">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('common.date')}</th>
                  <th className={th}>{t('common.name')}</th>
                  <th className={th}>{t('holidays.source')}</th>
                  {edit && <th className={clsx(th, 'w-20')} />}
                </tr>
              </thead>
              <tbody>
                {q.data.holidays.map((x) => (
                  <tr key={x.date} className="border-b border-line/70">
                    <td className={clsx(td, 'num whitespace-nowrap')}>{d(x.date)}</td>
                    <td className={td}>
                      {x.name}
                      {x.source === 'changed' && x.standardName && x.standardName !== x.name && <span className="block text-xs text-graphite">{t('holidays.wasName', { name: x.standardName })}</span>}
                      {x.fraction < 1 && (
                        <div className="mt-1">
                          <Pill tone="info">{t('workload.fractionHalf')}</Pill>
                        </div>
                      )}
                    </td>
                    <td className={td}>
                      <span className="flex flex-wrap gap-1">
                        <Pill tone={SOURCE_TONE[x.source as 'standard']}>{t(`holidays.sources.${x.source as 'standard'}`)}</Pill>
                        {x.override?.calendar === '*' && <Pill>{t('workload.allCalendars')}</Pill>}
                        {x.override?.yearly && <Pill tone="info">{t('holidays.yearly')}</Pill>}
                      </span>
                    </td>
                    {edit && (
                      <td className={clsx(td, 'whitespace-nowrap text-right')}>
                        <IconButton
                          label={t('common.edit')}
                          icon={<Pencil className="size-3.5" />}
                          onClick={() => {
                            // a yearly rule is edited as the rule itself (its first date), not as this year's day
                            const row = x.override?.yearly ? q.data!.overrides.find((o) => o.id === x.override!.id) : undefined;
                            const date = row?.date ?? x.date;
                            setForm({
                              calendar: x.override?.calendar ?? calendar,
                              date,
                              name: x.name,
                              fraction: x.fraction >= 1 ? 1 : 0.5,
                              yearly: !!x.override?.yearly,
                              note: x.override?.note ?? '',
                              moveFrom: date,
                              standard: x.source !== 'custom',
                            });
                          }}
                        />
                        <IconButton
                          label={x.source === 'custom' ? t('common.delete') : t('holidays.remove')}
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => (x.source === 'custom' && x.override ? dropOverride(x.override.id, t('holidays.deleted', { name: x.name })) : remove(x))}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-line bg-sheet p-4">
              <h3 className="mb-2 text-sm font-semibold">{t('holidays.removedTitle')}</h3>
              {q.data.removed.length === 0 && <p className="text-sm text-graphite">{t('common.noData')}</p>}
              <ul className="flex flex-col gap-1 text-sm">
                {q.data.removed.map((r) => (
                  <li key={r.date} className="flex items-center justify-between gap-2 border-b border-line/60 py-1">
                    <span className="text-graphite line-through">
                      <span className="num">{d(r.date)}</span> {r.name}
                    </span>
                    {edit && r.override && (
                      <Button size="sm" variant="ghost" onClick={() => dropOverride(r.override!.id, t('holidays.restored', { name: r.name }))}>
                        {t('holidays.restore')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-line bg-sheet p-4">
              <h3 className="mb-2 text-sm font-semibold">{t('workload.overrides')}</h3>
              {q.data.overrides.length === 0 && <p className="text-sm text-graphite">{t('common.noData')}</p>}
              <ul className="flex flex-col gap-1 text-sm">
                {q.data.overrides.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-2 border-b border-line/60 py-1">
                    <span>
                      <span className="num">{o.yearly ? d(o.date).slice(0, 6) : d(o.date)}</span> {o.name}{' '}
                      <Pill>{o.calendar === '*' ? t('workload.allCalendars') : o.calendar}</Pill>{' '}
                      <Pill tone="info">{o.fraction === 0 ? t('workload.fractionNone') : o.fraction < 1 ? t('workload.fractionHalf') : t('workload.fractionFull')}</Pill>
                      {o.yearly && <Pill tone="info">{t('holidays.yearly')}</Pill>}
                    </span>
                    {edit && <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => dropOverride(o.id, t('holidays.deleted', { name: o.name }))} />}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.moveFrom ? t('holidays.editTitle') : t('holidays.add')}
        footer={
          <>
            <Button onClick={() => setForm(null)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!form?.name.trim() || !form?.date} loading={set.isPending} onClick={() => form && void save(form)}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {form && (
          <div className="flex flex-col gap-3">
            <Field label={t('workload.calendar')} hint={form.standard ? t('holidays.calendarHintStandard') : undefined}>
              <Select value={form.calendar} disabled={form.standard} onChange={(e) => setForm({ ...form, calendar: e.target.value })}>
                <option value="*">{t('workload.allCalendars')}</option>
                {HOLIDAY_CALENDARS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('common.date')} hint={form.moveFrom && form.date !== form.moveFrom ? t('holidays.moveHint', { date: d(form.moveFrom) }) : undefined}>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label={t('common.name')}>
              <Input value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label={t('workload.fraction')}>
              <Select value={form.fraction} onChange={(e) => setForm({ ...form, fraction: Number(e.target.value) as 0 | 0.5 | 1 })}>
                <option value={1}>{t('workload.fractionFull')}</option>
                <option value={0.5}>{t('workload.fractionHalf')}</option>
                <option value={0}>{t('workload.fractionNone')}</option>
              </Select>
            </Field>
            <Checkbox label={t('holidays.yearlyLabel')} hint={t('holidays.yearlyHint')} checked={form.yearly} onChange={(e) => setForm({ ...form, yearly: e.target.checked })} />
            <Field label={t('leave.note')}>
              <Input value={form.note} maxLength={300} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </Field>
            <ErrorBox error={set.error} />
          </div>
        )}
      </Modal>
    </div>
  );
}
